import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Button, Typography, CircularProgress, Alert, Checkbox, FormControlLabel, Link } from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';

export interface FileUploadProps {
  onFileUpload: (file: File, email: string, country: string, privacyConsent: boolean, consentStorage?: boolean, consentImprovement?: boolean, industry?: string, stage?: string) => Promise<void>;
  onTextSubmit?: (text: string, email?: string) => Promise<void>;
  disabled?: boolean;
  accept?: string;
  maxFileSize?: number;
  maxFiles?: number;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileUpload,
  onTextSubmit,
  disabled = false,
  accept = '.pdf,.docx,.pptx',
  maxFileSize = 100 * 1024 * 1024, // 100MB default
  maxFiles = 1,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [textInput, setTextInput] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [industry, setIndustry] = useState('');
  const [stage, setStage] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Email validation function
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Handle email change with validation
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    if (newEmail && !validateEmail(newEmail)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError(null);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (file.size > maxFileSize) {
          setError(`File is too large. Maximum size is ${maxFileSize / 1024 / 1024}MB. Your file: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
          setSelectedFile(null);
          return;
        }
        setSelectedFile(file);
        setError(null); // Clear any previous errors
      }
    },
    [maxFileSize]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: accept.split(',').reduce((acc, ext) => {
      const trimmedExt = ext.trim();
      if (trimmedExt === '.pdf') {
        acc['application/pdf'] = ['.pdf'];
      } else if (trimmedExt === '.pptx') {
        acc['application/vnd.openxmlformats-officedocument.presentationml.presentation'] = ['.pptx'];
      } else if (trimmedExt === '.docx') {
        acc['application/vnd.openxmlformats-officedocument.wordprocessingml.document'] = ['.docx'];
      } else if (trimmedExt === '.txt') {
        acc['text/plain'] = ['.txt'];
      }
      return acc;
    }, {} as Record<string, string[]>),
    maxFiles,
    disabled,
  });



  const handleTextSubmit = async () => {
    if (!textInput.trim() || !onTextSubmit) return;
    
    try {
      setIsLoading(true);
      setError(null);
      await onTextSubmit(textInput, email || undefined);
    } catch (err) {
      console.error('Error submitting text:', err);
      setError(err instanceof Error ? err.message : 'Failed to analyze text');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!email) {
      setError('Email is required.');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!country) {
      setError('Country of Origin is required.');
      return;
    }

    if (!privacyConsent) {
      setError('You must agree to the privacy policy to continue.');
      return;
    }

    if (selectedFile) {
      try {
        setIsLoading(true);
        setError(null);
        // One bundled consent: agreeing to the Privacy Policy covers storage
        // and improvement use (details live on the /privacy-policy page)
        await onFileUpload(selectedFile, email, country, privacyConsent, privacyConsent, privacyConsent, industry, stage);
      } catch (err) {
        console.error('Error uploading file:', err);
        setError(err instanceof Error ? err.message : 'Failed to upload file');
      } finally {
        setIsLoading(false);
      }
    } else if (textInput.trim() && onTextSubmit) {
      await handleTextSubmit();
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Box
        component="div"
        sx={{ width: '100%' }}
      >
        <Typography variant="h5" gutterBottom align="center">
          Pitch Deck Analysis
        </Typography>
        
        {/* Email Input */}
        <Box sx={{ mb: 3, mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Your Email (required):
          </Typography>
          <Box
            component="input"
            type="email"
            value={email}
            onChange={handleEmailChange}
            placeholder="your.email@example.com"
            sx={{
              width: '100%',
              p: 1.5,
              border: '1px solid',
              borderColor: emailError ? 'error.main' : 'divider',
              borderRadius: 1,
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              '&:focus': {
                outline: 'none',
                borderColor: emailError ? 'error.main' : 'primary.main',
                boxShadow: emailError 
                  ? '0 0 0 2px rgba(211, 47, 47, 0.2)' 
                  : '0 0 0 2px rgba(25, 118, 210, 0.2)',
              },
            }}
            disabled={disabled || isLoading}
            required
          />
          {emailError && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {emailError}
            </Typography>
          )}
        </Box>

        {/* Country Input */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Country of Origin (required):
          </Typography>
          <Box
            component="input"
            type="text"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            placeholder="e.g., United States"
            sx={{
              width: '100%',
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              '&:focus': {
                outline: 'none',
                borderColor: 'primary.main',
                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
              },
            }}
            disabled={disabled || isLoading}
            required
          />
        </Box>

        {/* Industry Input (Optional) */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Industry (optional):
          </Typography>
          <Box
            component="input"
            type="text"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            placeholder="e.g., FinTech, HealthTech, SaaS"
            sx={{
              width: '100%',
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              '&:focus': {
                outline: 'none',
                borderColor: 'primary.main',
                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
              },
            }}
            disabled={disabled || isLoading}
          />
        </Box>

        {/* Stage Input (Optional) */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Funding Stage (optional):
          </Typography>
          <Box
            component="input"
            type="text"
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            placeholder="e.g., Pre-seed, Seed, Series A"
            sx={{
              width: '100%',
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              '&:focus': {
                outline: 'none',
                borderColor: 'primary.main',
                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
              },
            }}
            disabled={disabled || isLoading}
          />
        </Box>

        {/* Single consent checkbox — full details live on the Privacy Policy page */}
        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={privacyConsent}
                onChange={(e) => setPrivacyConsent(e.target.checked)}
                disabled={disabled || isLoading}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                I agree to the{' '}
                <Link
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                >
                  Privacy Policy
                </Link>
                , including how my pitch deck and contact details are stored and used as described there.
              </Typography>
            }
            sx={{ alignItems: 'flex-start' }}
          />
        </Box>

        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'text.secondary',
            borderRadius: 1,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            mb: 2,
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          <input {...getInputProps()} />
          <UploadIcon sx={{ fontSize: 48, mb: 1, color: 'text.secondary' }} />
          <Typography>
            {isDragActive
              ? 'Drop the files here...'
              : `Drag and drop a file here, or click to select (${accept})`}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Supported formats: {accept.split(',').join(', ')}
          {maxFileSize && ` (Max ${maxFileSize / 1024 / 1024}MB)`}
        </Typography>
      </Box>

      {onTextSubmit && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Or paste your text here:
          </Typography>
          <Box
            component="textarea"
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder="Paste your pitch deck text here..."
            sx={{
              width: '100%',
              minHeight: '100px',
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              lineHeight: 1.5,
              resize: 'vertical',
              '&:focus': {
                outline: 'none',
                borderColor: 'primary.main',
                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)',
              },
            }}
            disabled={disabled || isLoading}
          />
          <Button
            variant="contained"
            onClick={handleTextSubmit}
            disabled={!textInput.trim() || isLoading || disabled}
            fullWidth
            sx={{ mt: 1 }}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Analyze Text'}
          </Button>
        </Box>
      )}

      {selectedFile && (
        <Box sx={{ mt: 2, mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography>{selectedFile.name}</Typography>
            <Button 
              size="small" 
              onClick={removeFile} 
              disabled={isLoading || disabled}
            >
              Remove
            </Button>
          </Box>
        </Box>
      )}

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleSubmit}
        disabled={!selectedFile || !email || !country || !privacyConsent || !!emailError || isLoading || disabled}
        startIcon={isLoading ? <CircularProgress size={20} /> : null}
      >
        {isLoading ? 'Analyzing...' : 'Analyze'}
      </Button>
    </Box>
  );
};
